// client/src/pages/secure/VirtualStagingPage.js — AI virtual staging of listing photos.
// OFF unless the site names a provider. A staged image is a separate copy, is not used
// until the listing agent approves it, and is always labelled "Virtually staged".
import React, { useState, useEffect, useCallback } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import { Card, Empty, ErrorLine, Btn, fmtDate, input } from '../../components/secure/territory/Shared';
import { VirtualStagingBadge } from '../../components/shared/VirtualStagingBadge';

const words = (k) => String(k || '').replace(/_/g, ' ');

/** An image behind the staff login, fetched with the token. */
const AuthImage = ({ src, alt, staged }) => {
    const [url, setUrl] = useState(null);
    useEffect(() => {
        let gone = false, made = null;
        fetch(src, { headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` } })
            .then((r) => (r.ok ? r.blob() : null)).then((b) => { if (b && !gone) { made = URL.createObjectURL(b); setUrl(made); } }).catch(() => {});
        return () => { gone = true; if (made) URL.revokeObjectURL(made); };
    }, [src]);
    return (
        <div className="relative">
            {url ? <img src={url} alt={alt} className="w-full h-40 object-cover rounded border" /> : <div className="w-full h-40 bg-slate-100 rounded border" />}
            {staged && <VirtualStagingBadge />}
        </div>
    );
};

const VirtualStagingPage = () => {
    const api = useApiClient();
    const [st, setSt] = useState(null);
    const [props, setProps] = useState([]);
    const [pid, setPid] = useState('');
    const [d, setD] = useState(null);
    const [req, setReq] = useState({ media_id: '', room_type: 'living_room', style: 'modern' });
    const [msg, setMsg] = useState('');
    const [error, setError] = useState('');
    useEffect(() => {
        api.get('/api/virtual-staging/status').then(setSt).catch((e) => setError(e.message));
        api.get('/api/properties').then(setProps).catch(() => {});
    }, [api]);
    const load = useCallback(async () => { if (!pid) return; try { setD(await api.get(`/api/virtual-staging/properties/${pid}`)); } catch (e) { setError(e.message); } }, [api, pid]);
    useEffect(() => { load(); }, [load]);
    const run = async (fn) => { try { setError(''); const r = await fn(); if (r?.message) setMsg(r.message); load(); } catch (e) { setError(e.message); } };
    const originals = d ? d.media.filter((m) => !m.is_virtually_staged) : [];
    const staged = d ? d.media.filter((m) => m.is_virtually_staged) : [];
    const waiting = d ? d.jobs.filter((j) => j.job_status === 'ready') : [];
    return (
        <div className="p-4 md:p-6 space-y-4">
            <div><h1 className="text-2xl font-bold text-slate-800">Virtual Staging</h1>
                <p className="text-sm text-slate-600">Software staging of an empty-room photo. The original is never changed; the staged copy is used only after the listing agent approves it, and it is always labelled <strong>Virtually staged</strong> — on the image wherever it shows, and in captions and exports. Nothing may hide a defect or add a feature that is not there.</p></div>
            {st && !st.enabled && <p className="p-3 bg-amber-50 text-amber-900 rounded text-sm">{st.note}</p>}
            <ErrorLine error={error} />{msg && <p className="text-sm text-green-700">{msg}</p>}
            <select className={input} value={pid} onChange={(e) => { setPid(e.target.value); setMsg(''); }}>
                <option value="">Choose a listing…</option>{props.map((p) => <option key={p.property_id} value={p.property_id}>{p.street_address}, {p.city} ({p.status})</option>)}
            </select>
            {d && <>
                {waiting.length > 0 && (
                    <Card title="Waiting for your approval">
                        <div className="grid md:grid-cols-2 gap-4">{waiting.map((j) => (
                            <div key={j.job_id} className="space-y-2">
                                <div className="grid grid-cols-2 gap-2 text-xs"><div>Original<AuthImage src={d.media.find((m) => m.media_id === j.source_media_id)?.storage_path || ''} alt="original" /></div>
                                    <div>Staged ({words(j.room_type)}, {words(j.style)})<AuthImage src={`/api/virtual-staging/jobs/${j.job_id}/preview`} alt="staged" staged /></div></div>
                                <div className="flex gap-2"><Btn onClick={() => run(() => api.post(`/api/virtual-staging/jobs/${j.job_id}/approve`, {}))}>Approve — add to listing (labelled)</Btn>
                                    <Btn kind="secondary" onClick={() => run(() => api.post(`/api/virtual-staging/jobs/${j.job_id}/reject`, { note: window.prompt('Why? (optional)') || '' }))}>Reject</Btn></div>
                            </div>))}</div>
                    </Card>
                )}
                <Card title="Stage a photo">
                    {originals.length === 0 ? <Empty>Upload photos on the property page first.</Empty> : <>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{originals.map((m) => (
                            <label key={m.media_id} className={`cursor-pointer rounded p-1 ${String(req.media_id) === String(m.media_id) ? 'ring-2 ring-slate-600' : ''}`}>
                                <input type="radio" className="hidden" name="m" onChange={() => setReq({ ...req, media_id: m.media_id })} />
                                <AuthImage src={m.storage_path} alt={m.caption || 'photo'} /><span className="text-xs">{m.caption || `Photo #${m.media_id}`}</span></label>))}</div>
                        <div className="grid grid-cols-2 gap-2 mt-3">
                            <label className="text-xs">Room<select className={input} value={req.room_type} onChange={(e) => setReq({ ...req, room_type: e.target.value })}>{(st?.room_types || []).map((r) => <option key={r} value={r}>{words(r)}</option>)}</select></label>
                            <label className="text-xs">Style<select className={input} value={req.style} onChange={(e) => setReq({ ...req, style: e.target.value })}>{(st?.styles || []).map((r) => <option key={r} value={r}>{words(r)}</option>)}</select></label>
                        </div>
                        <div className="mt-2"><Btn disabled={!st?.enabled || !req.media_id} onClick={() => run(() => api.post(`/api/virtual-staging/properties/${pid}/jobs`, req))}>Make a staged copy</Btn></div>
                    </>}
                </Card>
                <Card title="Staged photos on this listing">
                    {staged.length === 0 ? <Empty>None.</Empty> : <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{staged.map((m) => (
                        <div key={m.media_id}><AuthImage src={m.storage_path} alt={m.caption} staged /><p className="text-xs mt-1">{m.caption} · original #{m.original_media_id}</p>
                            <Btn kind="danger" onClick={() => run(() => api.post(`/api/virtual-staging/media/${m.media_id}/withdraw`, {}))}>Take off the listing</Btn></div>))}</div>}
                </Card>
                <Card title="History">
                    {d.jobs.length === 0 ? <Empty>No staging yet.</Empty> : <ul className="text-sm divide-y">{d.jobs.map((j) => (
                        <li key={j.job_id} className="py-1">#{j.job_id} · photo #{j.source_media_id} · {words(j.room_type)} / {words(j.style)} · <strong>{j.job_status}</strong> · {j.provider} · {fmtDate(j.created_at)}{j.error_note ? ` · ${j.error_note}` : ''}{j.decision_note ? ` · ${j.decision_note}` : ''}</li>))}</ul>}
                </Card>
            </>}
        </div>
    );
};

export default VirtualStagingPage;
